import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { mediumGreen, lightGold } from "@styles/synchrony-core-palette.js";
import RadioCheckboxButton from "@components/ui/RadioCheckboxButton";
import Button from "@components/ui/Button";
import get from "lodash/get";
import {
  setFMLink,
  removeFMLink,
  setFMLinkEditingMode,
} from "@features/schema-management/actions";
import { setConfirmationDock, setAlertDock } from "@actions/global";
import commercialFmAdapter from "@features/files/adapters/commercial-files";

const CustomFieldWrapperTemplate = ({
  id,
  classNames,
  label,
  help,
  required,
  errors,
  children,
  fields,
  displayLabel,
  schema,
}) => {
  const schemaAllView = useSelector((state) => state.schemaAllView);
  const schemaConfigScheduler = useSelector(
    (state) => state.schemaConfigScheduler
  );
  const { initialScheduleDraft } = schemaConfigScheduler;
  const rootValue = id.toLowerCase().split(label.toLowerCase())[0] === "root.";

  const {
    changeDict,
    searchValue,
    isAthosConfig,
    fmLink,
    fmLinkBucket,
    fmLinkList,
    fmLinkEditingMode: editingMode,
    schemaManagementConfig: config,
  } = schemaAllView;

  const dispatch = useDispatch();
  const [pathID, setPathID] = useState("");
  const [hidden, setHidden] = useState("");
  const [linkField, setLinkField] = useState(false);
  const [checked, setChecked] = useState(false);
  const [fieldEligible, setFieldEligible] = useState(false);
  const [fieldValue, setFieldValue] = useState("");
  const [checkDisabled, setCheckDisabled] = useState(false);
  const linkFieldID = "_constants_link_";
  const withPattern = schema.pattern && new RegExp(schema.pattern).test("");
  const allowsEmptyValues = schema.hasOwnProperty("minLength")
    ? !(schema?.minLength > 0)
    : Array.isArray(schema.type)
    ? schema.type.includes("null")
    : schema.hasOwnProperty("pattern")
    ? withPattern
    : true;

  useEffect(() => {
    if (id && displayLabel) {
      let removedRoot = id.replace(/^root./, "");
      if (changeDict && changeDict.hasOwnProperty(removedRoot)) {
        setPathID(removedRoot);
      }
    }
  }, [id]);

  useEffect(() => {
    if (searchValue && rootValue && isAthosConfig) {
      if (!label.toLowerCase().includes(searchValue.toLowerCase())) {
        setHidden("hidden");
      } else {
        setHidden("");
      }
    }
  }, [searchValue]);

  useEffect(() => {
    if (id && id.includes(linkFieldID) && displayLabel) setLinkField(true);
  }, [id, linkFieldID, displayLabel]);

  useEffect(() => {
    if (linkField && fmLinkBucket) {
      let removedRoot = id.replace(/^root_/, "");

      let _fieldValue = get(config, removedRoot);
      var re = new RegExp(fmLinkBucket, "i");

      if (re.test(_fieldValue)) {
        if (
          fmLinkList &&
          fmLinkList.filter(
            (fmLink) => fmLinkBucket + fmLink.location_name === _fieldValue
          ).length > 0
        ) {
          setChecked(true);
          setCheckDisabled(true);
        } else {
          setChecked(false);
          setCheckDisabled(false);
        }
        setFieldEligible(true);
        setFieldValue(_fieldValue);
      } else setFieldEligible(false);
    }
  }, [id, linkField, fmLinkBucket, config]);

  useEffect(() => {
    // Remove link from store if it exists, forces user to re-select the checkbox
    if (Object.keys(fmLink).includes(id))
      dispatch(removeFMLink({ linkName: id }));
  }, [fieldValue]);

  useEffect(() => {
    if (fmLink[id]) setChecked(true);
    else setChecked(false);

    // Handle existing fm links
    let removedRoot = id.replace(/^root_/, "");

    let _fieldValue = get(config, removedRoot);

    if (
      fmLinkList &&
      fmLinkList.filter(
        (fmLink) => fmLinkBucket + fmLink.location_name === _fieldValue
      ).length > 0
    ) {
      setChecked(true);
      setCheckDisabled(true);
    }
  }, [id, fmLink, fmLinkList]);

  const createFMLinks = () => {
    let restrictedLinks = [];
    let aFMLinks = [];

    for (const link in fmLink) {
      let path = fmLink[link].split(fmLinkBucket)[1];
      if (!path || path.match(/[\\/#%&+;?"]/g)) {
        restrictedLinks.push({ id: link, link: fmLink[link] });
      } else {
        aFMLinks.push({ id: link, link: fmLink[link] });
      }
    }

    if (restrictedLinks && restrictedLinks.length > 0) {
      dispatch(
        setAlertDock({
          title: "Error Creating File Manager Links",
          message: (
            <div>
              {`The following link(s) contain restricted characters:`} <br />
              {restrictedLinks.map((fmLink) => (
                <div key={fmLink.id} className="fm-link">
                  <span className="fm-link-id">{fmLink.id}: </span>
                  <span className="fm-link-link">{fmLink.link}</span>
                </div>
              ))}
              Please edit this link before proceeding.
            </div>
          ),
        })
      );
    } else {
      dispatch(
        setConfirmationDock({
          title: "Create File Manager Links",
          message: `The following links will be created in File Manager:${aFMLinks.join(
            "\n\n"
          )}Do you wish to proceed?`,
          constructDiv: (
            <div className="fm-link-msg">
              The following links will be created in File Manager:
              <div className="fm-links-wrap">
                {aFMLinks.map((fmLink) => (
                  <div key={fmLink.id} className="fm-link">
                    <span className="fm-link-id">{fmLink.id}: </span>
                    <span className="fm-link-link">{fmLink.link}</span>
                  </div>
                ))}
              </div>
              Do you wish to proceed?
            </div>
          ),
          updateAction: "updateFMLinks",
          idTarget: "fm-links",
        })
      );
    }
  };

  const handleCheckBoxClick = async () => {
    if (!checked && fmLinkBucket) {
      let fileLocation = fieldValue.toLowerCase().split(fmLinkBucket)[1];
      let result = await commercialFmAdapter.fetchIndividualFileLocation(
        fileLocation
      );

      if (result.length > 0) {
        let report = {
          title: `There was an issue preparing the File Manager link!`,
          message: `${fmLinkBucket}${fileLocation} already exists.`,
        };

        dispatch(
          setAlertDock({
            title: report.title,
            message: report.message,
          })
        );
      } else
        dispatch(
          setFMLink({
            linkName: id,
            linkValue: fieldValue && fieldValue.toLowerCase(),
          })
        );
    } else dispatch(removeFMLink({ linkName: id }));
  };

  return (
    <>
      <div
        className={`${
          searchValue && isAthosConfig ? hidden : ""
        } !mb-3 !mt-1 !mx-2 ${classNames}
        ${linkField && "link-field"} ${editingMode && "editing"}`}
        //  ${
        //   linkField && "link-field"
        // } ${editingMode && "editing"}`}
        style={{
          ...(pathID && initialScheduleDraft.status
            ? {
                boxShadow: `0px 0px 5px 1px ${
                  initialScheduleDraft.status === "scheduled"
                    ? mediumGreen
                    : initialScheduleDraft.status === "draft"
                    ? lightGold
                    : ""
                }`,
              }
            : {}),
        }}
      >
        {displayLabel ? (
          <label htmlFor={id} className="control-label">
            {label}
            {!allowsEmptyValues ? "*" : null}
          </label>
        ) : null}
        {!displayLabel && label === "Link" && (
          <>
            {!editingMode ? (
              <div className="fm-links-btn-wrap !mr-28">
                <Button
                  label="Create Links in File Manager"
                  className="syf-login-action-create"
                  componentID="syf-login-action"
                  toUpperCase={false}
                  height="h-8"
                  customStyle={
                    "border-none text-sm text-white bg-sky-555 rounded-[4.66px] w-max pt-3 pr-5 inline-flex items-center justify-center m-0"
                  }
                  theme="secondary"
                  onClickCallback={(event) => {
                    event.preventDefault();
                    dispatch(setFMLinkEditingMode(true));
                  }}
                />
              </div>
            ) : (
              <div className="fm-links-btn-wrap !mr-28">
                <Button
                  height="h-8"
                  className="syf-login-action"
                  componentID="syf-login-action-create-sec"
                  toUpperCase={false}
                  customStyle={`${
                    Object.keys(fmLink).length !== 0
                      ? "text-white bg-sky-555"
                      : "text-[rgb(148, 150, 154)]bg-[rgb(224, 224, 224)]"
                  } border-none text-sm rounded-[4.66px] pt-3 pr-5 inline-flex items-center justify-center m-0`}
                  theme="secondary"
                  label="Create Links"
                  onClickCallback={(event) => {
                    event.preventDefault();
                    createFMLinks();
                  }}
                  disabled={Object.keys(fmLink).length !== 0 ? false : true}
                />
                <Button
                  className="syf-login-action"
                  componentID="syf-login-action-cancel"
                  label="Cancel"
                  toUpperCase={false}
                  height="h-8"
                  customStyle={
                    " border-none text-opacity-87 bg-transparent hover:bg-color-gray rounded-[4.66px] pt-3 pr-5 inline-flex text-sm items-center justify-center "
                  }
                  theme="secondary"
                  key="0"
                  onClickCallback={(event) => {
                    event.preventDefault();
                    dispatch(setFMLinkEditingMode(false));
                  }}
                />
              </div>
            )}
          </>
        )}
        {fieldEligible && (
          <div className="link-field-checkbox-wrap">
            <RadioCheckboxButton
              key={`${id}-checkbox`}
              containerStyle={
                "mr-2 p-0 absolute left-[-32px] top-[calc(50% - 12px)]"
              }
              onClickCallback={() => handleCheckBoxClick()}
              checked={checked}
              buttonStyle={"max-w-none min-w-[1.25rem]"}
              disabled={checkDisabled}
            />
          </div>
        )}
        {children}
        {errors}
        {help}
      </div>
    </>
  );
};

export default CustomFieldWrapperTemplate;
